import React, { Suspense, lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import LoadingScreen from './components/LoadingScreen';
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import AuthGuard from "./components/guards/AuthGuard";
import GuestGuard from "./components/guards/GuestGuard";
import MainLayout from "./components/MainLayout";
import BackofficeGuard from "./components/guards/BackofficeGuard";

const Loadable = (Component: React.ComponentType<any>) => (props: any) => (
    <Suspense fallback={<LoadingScreen />}>
        <Component {...props} />
    </Suspense>
);


// Error pages
const AuthorizationRequired = Loadable(lazy(() => import('./pages/guard/AuthorizationRequired')));
const NotFound = Loadable(lazy(() => import('./pages/guard/NotFound')));
const ServerError = Loadable(lazy(() => import('./pages/guard/ServerError')));

// Other pages
const Home = Loadable(lazy(() => import('./pages/Home')));
const Login = Loadable(lazy(() => import('./pages/authentication/Login')));
const Signup = Loadable(lazy(() => import('./pages/authentication/Signup')));
const PasswordRecovery = Loadable(lazy(() => import('./pages/authentication/PasswordRecovery')));

//PROPERTY BUILDING INVITATION
const PropertyInvitationList = Loadable(lazy(() => import('./pages/propertyInvitation/PropertyInvitationList')));

//BUILDING/PROPERTY
const EdificiListContent = Loadable(lazy(() => import ('./pages/building/list/BuildingList')));
const EdificiDetailContent = Loadable(lazy(() => import ('./pages/building/detail/BuildingDetail')));
const EdificiAddContent = Loadable(lazy(() => import ('./pages/building/add/BuildingAdd')));
const EdificiAmministrati = Loadable(lazy(() => import('./pages/EdificiAmministrati')));
const MyCommunities = Loadable(lazy(() => import('./pages/MyCommunities')));
const TerminiCondizioni = Loadable(lazy(() => import('./pages/TerminiCondizioni')));

const Privacy = Loadable(lazy(() => import('./pages/Privacy')));

//AUTH
const AuthUrl = Loadable(lazy(() => import('./pages/authentication/AuthUrl')));

const OwnerOnBoarding = Loadable(lazy(() => import('./pages/onboarding/OwnerOnBoarding')));
const BuildingOnBoarding = Loadable(lazy(() => import('./pages/onboarding/BuildingOnBoarding')));
const ContractorOnBoarding = Loadable(lazy(() => import('./pages/onboarding/ContractorOnBoarding')));
const ContractorCaitelInfo = Loadable(lazy(() => import('./pages/onboarding/ContractorCaitelInfo')));

const CentraleTermicaDocuments = Loadable(lazy(() => import('./pages/onboarding/CentraleTermicaDocuments')));

//New community
const NewCommunityStart = Loadable(lazy(() => import('./pages/newcommunity/NewCommunityStart')));
const NewCommunityScegliEdifici = Loadable(lazy(() => import('./pages/newcommunity/NewCommunityScegliEdifici')));
const NewCommunityCompilaDati = Loadable(lazy(() => import('./pages/newcommunity/NewCommunityCompilaDati')));
const NewCommunityCoinvolgi = Loadable(lazy(() => import('./pages/newcommunity/NewCommunityCoinvolgi')));
const NewCommunityEsaminaRisultati = Loadable(lazy(() => import('./pages/newcommunity/NewCommunityEsaminaRisultati')));
const NewCommunityDefinisciConfigurazione = Loadable(lazy(() => import('./pages/newcommunity/NewCommunityDefinisciConfigurazione')));
const NewCommunityDeliberaInAssemblea = Loadable(lazy(() => import('./pages/newcommunity/NewCommunityDeliberaInAssemblea')));

//UPLOAD
const NewCommunityDocumentiProgettista = Loadable(lazy(() => import('./pages/newcommunity/NewCommunityDocumentiProgettista')));
const NewCommunityDocumentiInstallatore = Loadable(lazy(() => import('./pages/newcommunity/NewCommunityDocumentiInstallatore')));
const NewCommunityDocumentiMembri = Loadable(lazy(() => import('./pages/newcommunity/NewCommunityDocumentiMembri')));

const SurveyStart = Loadable(lazy(() => import('./pages/survey/SurveyStart')));
const SurveyDoing = Loadable(lazy(() => import('./pages/survey/SurveyDoing')));

//BO
const BOGestioneUserList = Loadable(lazy(()=>import('./pages/backoffice/utenti/BOGestioneUserList')))
const BOAddUser = Loadable(lazy(()=>import('./pages/backoffice/utenti/BOAddUser')))
const BOBuildingStatusPayment = Loadable(lazy(() => import('./pages/backoffice/BOBuildingStatusPayment')));
const BOGestioneCommunities = Loadable(lazy(() => import('./pages/backoffice/BOGestioneCommunities')));
const BOGestioneDetailCommunity = Loadable(lazy(() => import('./pages/backoffice/gestionecomunita/BOGestioneDetailCommunity')));

const BOGestioneCommunitiesList = Loadable(lazy(() => import('./pages/backoffice/gestionecomunita/BOGestioneCommunitiesList')));
const BOGestioneCommunitiesUnderDevList = Loadable(lazy(() => import('./pages/backoffice/gestionecomunita/BOGestioneCommunitiesUnderDevList')));

const BOCommunityCoperture = Loadable(lazy(() => import('./pages/backoffice/coperture/BOCommunityCoperture')));
const BOCopertura = Loadable(lazy(() => import('./pages/backoffice/copertura/BOCopertura')));

const BOImportBulk = Loadable(lazy(() => import('./pages/backoffice/BOImportBulk')));

//MANUTENTORE:
const ProfileInfo = Loadable(lazy(() => import('./pages/profile/Profile')));

//PRINT
const DiagnosiPrestazioniPrintPage = Loadable(
    lazy(() => import('./pages/building/print/prestazioni/DiagnosiPrestazioniPrintPage'))
);
const BuildingDataPrintPage = Loadable(
    lazy(() => import('./pages/building/print/data/BuildingDataPrintPage'))
);
const RoofingPreviewPrintPage = Loadable(
    lazy(() => import('./pages/building/print/roofing/RoofingPreviewPrintPage'))
);



//EVENTUALE REGISTER
const routes: RouteObject[] = [
  {
    path: '/',                     // il root ora è "/"
    element: <MainLayout />,
    children: [
      // Home: index route per "/"
      {
        index: true,
        element: (
            /*
            Changed 08/05/2026
            <AuthGuard>
              <Home />
            </AuthGuard>*/
            <AuthGuard>
                <EdificiListContent />
            </AuthGuard>
        )
      },

      // AUTH
      {
        path: 'login',             // "/login"
        element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
        )
      },
      {
        path: 'password-recovery', // "/password-recovery"
        element: (
            <GuestGuard>
              <PasswordRecovery />
            </GuestGuard>
        )
      },
      {
        path: 'signup',            // "/signup"
        element: (
            <GuestGuard>
              <Signup />
            </GuestGuard>
        )
      },

      // STATICHE
      { path: 'contact', element: <Contact /> },          // "/contact"
      { path: 'faq', element: <FAQ /> },                 // "/faq"
      { path: 'tos', element: <TerminiCondizioni /> },   // "/tos"
        // "/tosamm"
      { path: 'privacy', element: <Privacy /> },         // "/privacy"

      {
        path: 'auth',                                    // "/auth"
        element: (
            <GuestGuard>
              <AuthUrl />
            </GuestGuard>
        )
      },

      // EDIFICI / ABBONAMENTI
      {
        path: 'subscriptions',                           // "/subscriptions"
        element: (
            <AuthGuard>
              <EdificiAmministrati />
            </AuthGuard>
        )
      },

      {
        path: 'building',                                // "/building"
        children: [
          {
            index: true,                                 // "/building"
            element: (
                <AuthGuard>
                  <EdificiListContent />
                </AuthGuard>
            )
          },
          {
            path: 'add',                                 // "/building/add"
            element: (
                <AuthGuard>
                  <EdificiAddContent />
                </AuthGuard>
            )
          },
          {
            path: 'edit/:idProperty',                    // "/building/edit/:idProperty"
            element: (
                <AuthGuard>
                  <EdificiAddContent />
                </AuthGuard>
            )
          },
          {
            path: 'detail/:idBuilding',                  // "/building/detail/:idBuilding"
            element: (
                <AuthGuard>
                  <EdificiDetailContent />
                </AuthGuard>
            )
          },
          {
            path: 'invitations',                         // "/building/invitations"
            element: (
                <AuthGuard>
                  <PropertyInvitationList />
                </AuthGuard>
            )
          }
        ]
      },

      // PROFILO / COMUNITÀ
      {
        path: 'profile',                                 // "/profile"
        element: (
            <AuthGuard>
              <ProfileInfo />
            </AuthGuard>
        )
      },
      {
        path: 'mycommunities',                           // "/mycommunities"
        element: (
            <AuthGuard>
              <MyCommunities />
            </AuthGuard>
        )
      },

      // ONBOARDING
        { path: 'onboarding/owner', element: <OwnerOnBoarding /> },
        { path: 'onboarding/contractor', element: <ContractorOnBoarding /> },
        { path: 'onboarding/building', element: <BuildingOnBoarding /> },
        { path: 'caitelData', element: <ContractorCaitelInfo /> },
        { path: 'centraleTermicaDocuments', element: <CentraleTermicaDocuments /> },


        // SURVEY
      {
        path: 'survey',
        children: [
          {
            path: 'start/:base64IdCommunity',            // "/survey/start/:base64IdCommunity"
            element: (
                  <SurveyStart />
            )
          },
          {
            path: 'doing',                               // "/survey/doing"
            element: (
                  <SurveyDoing />
            )
          }
        ]
      },

      // BACKOFFICE
      {
        path: 'bo',
        children: [
          {
            path: 'buildings',                           // "/bo/buildings"
            element: (
                <BackofficeGuard>
                  <BOBuildingStatusPayment />
                </BackofficeGuard>
            )
          },
          {
            path: 'users',
            children: [
              {
                index: true,                             // "/bo/users"
                element: (
                    <BackofficeGuard>
                      <BOGestioneUserList />
                    </BackofficeGuard>
                )
              },
              {
                path: 'add',                             // "/bo/users/add"
                element: (
                    <BackofficeGuard>
                      <BOAddUser />
                    </BackofficeGuard>
                )
              },
              {
                path: 'edit/:idUser',                    // "/bo/users/edit/:idUser"
                element: (
                    <BackofficeGuard>
                      <BOAddUser />
                    </BackofficeGuard>
                )
              }
            ]
          },
          {
            path: 'communities',
            children: [
              {
                index: true,                             // "/bo/communities"
                element: (
                    <BackofficeGuard>
                      <BOGestioneCommunities />
                    </BackofficeGuard>
                )
              },
              {
                path: 'detail',                         // "/bo/communities/detail"
                element: (
                    <BackofficeGuard>
                      <BOGestioneDetailCommunity />
                    </BackofficeGuard>
                )
              },
              {
                path: 'underDev',                       // "/bo/communities/underDev"
                element: (
                    <BackofficeGuard>
                      <BOGestioneCommunitiesUnderDevList />
                    </BackofficeGuard>
                )
              },
              {
                path: 'handled',                        // "/bo/communities/handled"
                element: (
                    <BackofficeGuard>
                      <BOGestioneCommunitiesList />
                    </BackofficeGuard>
                )
              }
            ]
          },
          {
            path: 'coperture',                          // "/bo/coperture"
            element: (
                <BackofficeGuard>
                  <BOCommunityCoperture />
                </BackofficeGuard>
            )
          },
          {
            path: 'copertura',                          // "/bo/copertura"
            element: (
                <BackofficeGuard>
                  <BOCopertura />
                </BackofficeGuard>
            )
          },
          {
            path: 'importBulk',                         // "/bo/importBulk"
            element: (
                <BackofficeGuard>
                  <BOImportBulk />
                </BackofficeGuard>
            )
          }
        ]
      },

      // NEW COMMUNITY
      {
        path: 'newcommunity',
        children: [
          {
            index: true,                                // "/newcommunity"
            element: (
                <AuthGuard>
                  <NewCommunityStart />
                </AuthGuard>
            )
          },
          {
            path: 'scegliEdifici',                      // "/newcommunity/scegliEdifici"
            element: (
                <AuthGuard>
                  <NewCommunityScegliEdifici />
                </AuthGuard>
            )
          },
          {
            path: 'compiladati',                        // "/newcommunity/compiladati"
            element: (
                <AuthGuard>
                  <NewCommunityCompilaDati />
                </AuthGuard>
            )
          },
          {
            path: 'coinvolgi',                          // "/newcommunity/coinvolgi"
            element: (
                <AuthGuard>
                  <NewCommunityCoinvolgi />
                </AuthGuard>
            )
          },
          {
            path: 'esaminarisultati',                   // "/newcommunity/esaminarisultati"
            element: (
                <AuthGuard>
                  <NewCommunityEsaminaRisultati />
                </AuthGuard>
            )
          },
          {
            path: 'definisciConfigurazione',            // "/newcommunity/definisciConfigurazione"
            element: (
                <AuthGuard>
                  <NewCommunityDefinisciConfigurazione />
                </AuthGuard>
            )
          },
          {
            path: 'deliberaInAssemblea',                // "/newcommunity/deliberaInAssemblea"
            element: (
                <AuthGuard>
                  <NewCommunityDeliberaInAssemblea />
                </AuthGuard>
            )
          },
          {
            path: 'document/upload/progettista/:base64',
            element: (
                <AuthGuard>
                  <NewCommunityDocumentiProgettista />
                </AuthGuard>
            )
          },
          {
            path: 'document/upload/installatore/:base64',
            element: (
                <AuthGuard>
                  <NewCommunityDocumentiInstallatore />
                </AuthGuard>
            )
          },
          {
            path: 'document/upload/membri/:base64',
            element: (
                <AuthGuard>
                  <NewCommunityDocumentiMembri />
                </AuthGuard>
            )
          }
        ]
      },
// REPORT PRINT
      // ERROR PAGES
      { path: '401', element: <AuthorizationRequired /> },
      { path: '404', element: <NotFound /> },
      { path: '500', element: <ServerError /> },

      // CATCH-ALL
      { path: '*', element: <NotFound /> }
    ]
  },
    {
        path: 'report/diagnosi-print',
        element: <DiagnosiPrestazioniPrintPage />
    },
    {
        path: 'report/buildingdata-print',
        element: <BuildingDataPrintPage />
    },
    {
        path: 'report/roofing-preview-print',
        element: <RoofingPreviewPrintPage />
    },
];

export default routes;
